import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { CheckCircle, XCircle, Truck } from "lucide-react";
import type { DeliveryAdjustmentWithDetails } from "@shared/schema";

type Tab = "pending" | "reviewed";

export default function AdminDeliveryAdjustmentsPage() {
  const [tab, setTab] = useState<Tab>("pending");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: adjustments, isLoading } = useQuery<DeliveryAdjustmentWithDetails[]>({
    queryKey: ["/api/admin/delivery-adjustments", tab],
    queryFn: () =>
      fetch(`/api/admin/delivery-adjustments?status=${tab === "pending" ? "pending" : "reviewed"}`)
        .then(r => r.json()),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/admin/delivery-adjustments/${id}/approve`, {}),
    onSuccess: () => {
      toast({ title: "Avvikelse godkänd" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/delivery-adjustments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/delivery-adjustments/count"] });
    },
    onError: () => toast({ title: "Fel", description: "Kunde inte godkänna avvikelsen", variant: "destructive" }),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/admin/delivery-adjustments/${id}/reject`, {}),
    onSuccess: () => {
      toast({ title: "Avvikelse avvisad" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/delivery-adjustments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/delivery-adjustments/count"] });
    },
    onError: () => toast({ title: "Fel", description: "Kunde inte avvisa avvikelsen", variant: "destructive" }),
  });

  const statusBadge = (status: string) => {
    if (status === "approved") return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Godkänd</Badge>;
    if (status === "rejected") return <Badge variant="secondary">Avvisad</Badge>;
    return <Badge variant="outline">Väntande</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Truck className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Leveransavvikelser</h1>
          <p className="text-muted-foreground text-sm">Granska och hantera avvikelser från leveranser</p>
        </div>
      </div>

      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "pending" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          onClick={() => setTab("pending")}
          data-testid="tab-pending"
        >
          Väntande
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "reviewed" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          onClick={() => setTab("reviewed")}
          data-testid="tab-reviewed"
        >
          Granskade
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : !adjustments || adjustments.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="text-empty">
          {tab === "pending" ? "Inga väntande avvikelser" : "Inga granskade avvikelser"}
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Order #</th>
                <th className="px-4 py-3 text-left font-medium">Kund</th>
                <th className="px-4 py-3 text-left font-medium">Produkt</th>
                <th className="px-4 py-3 text-right font-medium">Beställt</th>
                <th className="px-4 py-3 text-right font-medium">Levererat</th>
                <th className="px-4 py-3 text-left font-medium">Anledning</th>
                <th className="px-4 py-3 text-left font-medium">Datum</th>
                {tab === "pending" ? (
                  <th className="px-4 py-3 text-left font-medium">Åtgärd</th>
                ) : (
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {adjustments.map((adj) => (
                <tr key={adj.id} className="hover:bg-muted/30 transition-colors" data-testid={`row-adjustment-${adj.id}`}>
                  <td className="px-4 py-3 font-medium">#{adj.orderNumber}</td>
                  <td className="px-4 py-3">{adj.clientName}</td>
                  <td className="px-4 py-3">{adj.productName}</td>
                  <td className="px-4 py-3 text-right" data-testid={`text-ordered-${adj.id}`}>{adj.orderedQty}</td>
                  <td className="px-4 py-3 text-right" data-testid={`text-delivered-${adj.id}`}>
                    <span className={adj.deliveredQty < adj.orderedQty ? "text-destructive font-medium" : ""}>
                      {adj.deliveredQty}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate" title={adj.reason || ""}>
                    {adj.reason || "—"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {adj.requestedAt ? format(new Date(adj.requestedAt), "d MMM yyyy", { locale: sv }) : "—"}
                  </td>
                  {tab === "pending" ? (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white gap-1"
                          onClick={() => approveMutation.mutate(adj.id)}
                          disabled={approveMutation.isPending || rejectMutation.isPending}
                          data-testid={`button-approve-${adj.id}`}
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                          Godkänn
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          onClick={() => rejectMutation.mutate(adj.id)}
                          disabled={approveMutation.isPending || rejectMutation.isPending}
                          data-testid={`button-reject-${adj.id}`}
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          Avvisa
                        </Button>
                      </div>
                    </td>
                  ) : (
                    <td className="px-4 py-3">
                      {statusBadge(adj.status)}
                      {adj.reviewedBy && (
                        <span className="block text-xs text-muted-foreground mt-0.5">{adj.reviewedBy}</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
